/**
 * Nominal (brand) types — Phase 2 Stream M.
 *
 * Why this file exists:
 *   Phase 1 shipped 4 smokes (#3, #4, #9, camelCase boundary) that
 *   shared the same DNA: two sides of a contract agreed on the WRONG
 *   convention. The "share_pct" case was the most embarrassing —
 *   `(p * 100).toFixed(0)` rendered `5920%` instead of `59%` because
 *   the BE shipped 0–100 but the FE assumed 0–1.
 *
 *   Unit tests didn't catch it. The values typecheck because both are
 *   `number`. The brand types here make the FE refuse to compile a
 *   `Percent * 100` or a `Fraction * 100` without an explicit
 *   conversion through `toPercent` / `toFraction`. The convention
 *   shows up in the type signature, not in a code comment that
 *   degrades over time.
 *
 * Design — opaque types, NOT `number & { brand }`:
 *   The widely-cited `number & {readonly __brand: 'Percent'}` pattern
 *   still allows `p * 100` to compile because TS treats `number & X`
 *   as `number` for operator resolution. To make arithmetic a
 *   compile error, the brand types here do NOT extend `number`. They
 *   are pure opaque tags; the runtime value is a number, the
 *   type-system view is a tagged box. Helpers (`asPercent`,
 *   `formatPercent`, `toFraction`) are the ONLY supported way to
 *   move in and out of the brand.
 *
 *   `BBL10` / `BIN7` follow the same pattern over `string`. The
 *   `String.prototype` methods (`.startsWith`, `.toLowerCase`, etc.)
 *   are intentionally NOT available on the brand — callers route
 *   through helpers or call `.toString()` (which they can't on an
 *   opaque value either — they `unwrapBbl10(b)` instead).
 *
 *   The trade-off is one of ergonomics: every consumer that wants the
 *   raw number/string back has to call an explicit unwrap. That's
 *   the point — the unwrap is the lift-of-attention moment that
 *   surfaces "you are about to do arithmetic on a percent, is that
 *   really what you want?"
 *
 * Runtime cost: zero. All brands erase at compile time. The
 *   constructors do range validation but the returned value IS the
 *   original number/string with a TS-level tag.
 */

// ---------------------------------------------------------------------
// Percent (0 ≤ n ≤ 100) — the BE convention for `share_pct` fields.
// ---------------------------------------------------------------------

declare const PercentBrand: unique symbol;

/**
 * A number in the range 0–100 inclusive, semantically a percentage.
 *
 * NOT a `number` from the type system's perspective: arithmetic on a
 * `Percent` is a compile error. Use {@link toFraction} to convert,
 * {@link formatPercent} to render, {@link unwrapPercent} to escape
 * to a raw `number` when interop demands it.
 */
export type Percent = { readonly [PercentBrand]: number };

/** Range validator + brand constructor. Throws on out-of-range / NaN. */
export const asPercent = (n: number): Percent => {
  if (typeof n !== "number" || Number.isNaN(n)) {
    throw new TypeError(`Percent must be a number; got ${String(n)}`);
  }
  if (n < 0 || n > 100) {
    throw new RangeError(`Percent out of range [0,100]: ${n}`);
  }
  return n as unknown as Percent;
};

/**
 * Permissive constructor — clamps to [0,100] instead of throwing.
 * Use this at the consumer boundary when the BE may have rounded
 * floats slightly outside the closed interval (e.g. 100.0000001).
 */
export const asPercentClamped = (n: number): Percent => {
  if (typeof n !== "number" || Number.isNaN(n)) {
    throw new TypeError(`Percent must be a number; got ${String(n)}`);
  }
  if (n < 0) return 0 as unknown as Percent;
  if (n > 100) return 100 as unknown as Percent;
  return n as unknown as Percent;
};

/** Escape-hatch to a raw number for interop with `Intl` / `toFixed`. */
export const unwrapPercent = (p: Percent): number => p as unknown as number;

// ---------------------------------------------------------------------
// Fraction (0 ≤ n ≤ 1) — the OTHER common percent encoding.
// ---------------------------------------------------------------------

declare const FractionBrand: unique symbol;

/** A number in the range 0–1 inclusive, semantically a fraction. */
export type Fraction = { readonly [FractionBrand]: number };

export const asFraction = (n: number): Fraction => {
  if (typeof n !== "number" || Number.isNaN(n)) {
    throw new TypeError(`Fraction must be a number; got ${String(n)}`);
  }
  if (n < 0 || n > 1) {
    throw new RangeError(`Fraction out of range [0,1]: ${n}`);
  }
  return n as unknown as Fraction;
};

export const unwrapFraction = (f: Fraction): number => f as unknown as number;

// ---------------------------------------------------------------------
// Conversions — explicit, branded, no implicit coercion.
// ---------------------------------------------------------------------

/** Multiply by 100 — the ONLY supported way to widen a Fraction. */
export const toPercent = (f: Fraction): Percent =>
  asPercent(unwrapFraction(f) * 100);

/** Divide by 100 — the ONLY supported way to narrow a Percent. */
export const toFraction = (p: Percent): Fraction =>
  asFraction(unwrapPercent(p) / 100);

// ---------------------------------------------------------------------
// Percent rendering — the boundary at which a Percent becomes a string.
// ---------------------------------------------------------------------

/**
 * Format a `Percent` for display. Default precision is 0 decimal
 * places (the LL97 / penalty-breakdown convention). Pass
 * `{ digits: 1 }` for finer rounding when the data warrants it.
 */
export const formatPercent = (
  p: Percent,
  options: { digits?: number } = {},
): string => {
  const digits = options.digits ?? 0;
  return `${unwrapPercent(p).toFixed(digits)}%`;
};

// ---------------------------------------------------------------------
// BBL10 — 10-character NYC Borough-Block-Lot.
// ---------------------------------------------------------------------

declare const Bbl10Brand: unique symbol;

/**
 * NYC BBL — borough prefix (1=Manhattan, 2=Bronx, 3=Brooklyn,
 * 4=Queens, 5=Staten Island), 5-digit block, 4-digit lot. Always 10
 * characters; always all digits.
 */
export type BBL10 = { readonly [Bbl10Brand]: string };

const BBL10_RE = /^[1-5]\d{9}$/;

export const isBbl10 = (s: unknown): s is BBL10 =>
  typeof s === "string" && BBL10_RE.test(s);

export const asBbl10 = (s: string): BBL10 => {
  if (!BBL10_RE.test(s)) {
    throw new TypeError(
      `BBL10 invalid: must be 10 digits with borough prefix 1-5, got "${s}"`,
    );
  }
  return s as unknown as BBL10;
};

export const unwrapBbl10 = (b: BBL10): string => b as unknown as string;

// ---------------------------------------------------------------------
// BIN7 — 7-digit NYC Building Identification Number.
// ---------------------------------------------------------------------

declare const Bin7Brand: unique symbol;

/**
 * NYC BIN — 7 digits. (DOB BINs are always 7 digits; the leading
 * digit encodes the borough but the FE doesn't slice it apart.)
 */
export type BIN7 = { readonly [Bin7Brand]: string };

const BIN7_RE = /^\d{7}$/;

export const isBin7 = (s: unknown): s is BIN7 =>
  typeof s === "string" && BIN7_RE.test(s);

export const asBin7 = (s: string): BIN7 => {
  if (!BIN7_RE.test(s)) {
    throw new TypeError(`BIN7 invalid: must be exactly 7 digits, got "${s}"`);
  }
  return s as unknown as BIN7;
};

export const unwrapBin7 = (b: BIN7): string => b as unknown as string;
